// ###### IMPORTS
import React, { useState, useEffect } from 'react';
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/solid';
import SpinnerLoad from '../../moduleComponents/SpinnerLoad';

// ###### MODAL EDIT CATEGORY COMPONENT
const ModalEditCategory = ({ category, onClose, onSave, onDelete }) => {
  // ###### STATES
  const [editedCategory, setEditedCategory] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Helper to build hierarchy string
  const getHierarchyString = () => {
    if (!category) return '';
    
    // Only show type for L1 and L2 (not for L0)
    const typeLabel = category.type && category.level > 0 ? ` (${category.type})` : '';
    
    if (category.level === 0) {
      // For L0: show only the clean name (no type)
      return category.l0 || category.name || category.id;
    }
    if (category.level === 1) {
      // For L1: show "L0 → L1 (Type)"
      return [category.l0, category.l1 || category.name].filter(Boolean).join(' → ') + typeLabel;
    }
    if (category.level === 2) {
      // For L2: show "L0 → L1 → L2 (Type)"
      return [category.l0, category.l1, category.l2 || category.name].filter(Boolean).join(' → ') + typeLabel;
    }
    return category.name || category.id;
  };

  // ###### EFFECTS
  useEffect(() => {
    if (category) {
      setEditedCategory({
        id: category.id,
        name: category.name,
        level: category.level
      });
    }
  }, [category]);

  // ###### FUNCTIONS
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedCategory(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await onSave(editedCategory);
      onClose();
    } catch (error) {
      console.error('Error saving category:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // ###### RENDER
  if (!category || !editedCategory) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-4 md:p-6 rounded-2xl shadow w-80 md:w-96 relative">
        {/* Loading Overlay */}
        {submitting && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="bg-white/90 rounded-xl shadow-lg p-4 border border-gray-200">
              <SpinnerLoad />
            </div>
          </div>
        )}
        
        {/* Close Button */}
        <div className="absolute top-2 right-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Hierarchy Display */}
        <div className="mb-4 p-2 bg-gray-100 rounded text-center text-gray-700 font-medium">
          {getHierarchyString()}
        </div>

        {/* Modal Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              name="name"
              value={editedCategory.name}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              placeholder="Category Name"
            />
          </div>
          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={() => {
                onDelete({ level: category.level, id: category.id, name: category.name });
                onClose();
              }}
              disabled={submitting}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <TrashIcon className="h-5 w-5" />
              Delete
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalEditCategory;
